// Dynamic API Service using Axios
// Centralized HTTP client with auth header handling and consistent responses

import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Attach fresh token on every request
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });
  }

  success(result) {
    return {
      success: true,
      data: result?.data?.data ?? result?.data,
      message: result?.data?.message || "Success",
    };
  }

  failure(endpoint, method, error) {
    const serverData = error?.response?.data;
    const serverMessage = serverData?.message;
    const serverErrors = serverData?.errors;
    const message = serverMessage || error?.message || "Network error occurred";
    console.error(`API Error [${method} ${endpoint}]:`, error);
    // Prefer detailed validation errors when available
    return {
      success: false,
      error: typeof serverErrors === "string" ? serverErrors : message,
      message, // keep top-level message for reference
      errors: serverErrors ?? null,
      data: null,
    };
  }

  async get(endpoint, params = {}) {
    try {
      const res = await this.client.get(endpoint, { params });
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "GET", e);
    }
  }

  async post(endpoint, data = {}) {
    try {
      const res = await this.client.post(endpoint, data);
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "POST", e);
    }
  }

  async put(endpoint, data = {}) {
    try {
      const res = await this.client.put(endpoint, data);
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "PUT", e);
    }
  }

  async putFormData(endpoint, formData) {
    try {
      const res = await this.client.put(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "PUT_FORMDATA", e);
    }
  }

  async patch(endpoint, data = {}) {
    try {
      const res = await this.client.patch(endpoint, data);
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "PATCH", e);
    }
  }

  async patchFormData(endpoint, formData) {
    try {
      const res = await this.client.patch(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "PATCH_FORMDATA", e);
    }
  }

  async delete(endpoint) {
    try {
      const res = await this.client.delete(endpoint);
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "DELETE", e);
    }
  }

  async postFormData(endpoint, formData) {
    try {
      const res = await this.client.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return this.success(res);
    } catch (e) {
      return this.failure(endpoint, "POST_FORMDATA", e);
    }
  }
}

const apiService = new ApiService();
export default apiService;
